// Content splitting utility for intelligent page breaks
export class ContentSplitter {
  constructor() {
    // Remove unreliable pixel conversion - use CSS-based measurements instead
  }

  // Split HTML content at natural break points
  splitContentAtHeight(htmlContent, maxHeightMm) {
    // Simple approach: if content would overflow, split at 70% mark
    console.log('🔍 CONTENT SPLITTER INPUT:', {
      contentLength: htmlContent?.length || 0,
      maxHeightMm,
      hasContent: !!htmlContent
    });
    
    // Create a temporary div to measure FULL content height
    const tempDiv = document.createElement('div');
    tempDiv.style.width = '190mm';
    tempDiv.style.fontSize = '12px';
    tempDiv.style.lineHeight = '1.4';
    tempDiv.style.fontFamily = 'inherit';
    tempDiv.style.padding = '12px 16px';
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.top = '-9999px';
    tempDiv.style.left = '-9999px';
    
    document.body.appendChild(tempDiv);
    
    try {
      // Test full content height
      tempDiv.innerHTML = htmlContent || '';
      const fullContentHeight = tempDiv.offsetHeight;
      const maxHeightPx = this.mmToCssPixels(maxHeightMm);
      
      console.log('📐 HEIGHT MEASUREMENT:', {
        fullContentHeight,
        maxHeightPx,
        exceedsMax: fullContentHeight > maxHeightPx
      });
      
      if (fullContentHeight <= maxHeightPx) {
        // Content fits entirely
        return {
          currentPageContent: htmlContent,
          nextPageContent: '',
          splitOccurred: false
        };
      }
      
      // Content exceeds height - split it
      return this.performSimpleSplit(htmlContent, tempDiv, maxHeightPx);
      
    } finally {
      document.body.removeChild(tempDiv);
    }
  }

  // Simplified splitting approach
  performSimpleSplit(htmlContent, measureDiv, maxHeight) {
    // Split content by paragraphs/lines for simpler logic
    const lines = htmlContent.split(/<\/?(div|p|br)[^>]*>/i).filter(line => line.trim());
    
    console.log('📄 SPLITTING LINES:', {
      totalLines: lines.length,
      maxHeight
    });
    
    if (lines.length <= 1) {
      // Single line/paragraph - split at 70% character mark
      const splitPoint = Math.floor(htmlContent.length * 0.7);
      return {
        currentPageContent: htmlContent.substring(0, splitPoint) + '...',
        nextPageContent: htmlContent.substring(splitPoint),
        splitOccurred: true
      };
    }
    
    // Test adding lines one by one until we exceed height
    let currentContent = '';
    let splitIndex = 0;
    
    for (let i = 0; i < lines.length; i++) {
      const testContent = lines.slice(0, i + 1).join('<br>');
      measureDiv.innerHTML = testContent;
      
      if (measureDiv.scrollHeight > maxHeight && i > 0) {
        // This line would exceed - split before it
        splitIndex = i;
        break;
      }
      
      currentContent = testContent;
    }
    
    if (splitIndex === 0) splitIndex = Math.floor(lines.length * 0.7); // Fallback to 70%
    
    const currentPageContent = lines.slice(0, splitIndex).join('<br>');
    const nextPageContent = lines.slice(splitIndex).join('<br>');
    
    console.log('✂️ SPLIT PERFORMED:', {
      splitIndex,
      currentPageLines: splitIndex,
      nextPageLines: lines.length - splitIndex,
      splitOccurred: true
    });
    
    return {
      currentPageContent,
      nextPageContent,
      splitOccurred: true
    };
  }

  // More reliable CSS-based conversion
  mmToCssPixels(mm) {
    // Create a temporary element with CSS mm units
    const tempEl = document.createElement('div');
    tempEl.style.height = `${mm}mm`;
    tempEl.style.position = 'absolute';
    tempEl.style.visibility = 'hidden';
    tempEl.style.top = '-9999px';
    
    document.body.appendChild(tempEl);
    const heightPx = tempEl.offsetHeight;
    document.body.removeChild(tempEl);
    
    console.log('🔄 MM TO PX CONVERSION:', { mm, heightPx });
    return heightPx;
  }

  // Calculate available height for content on a page
  calculateAvailableHeight(pageContext) {
    const totalPageHeight = 277; // A4 height in mm
    const headerHeight = 25;
    const footerHeight = 15;
    const usedHeight = pageContext.usedHeight || 0;
    const buffer = 10; // Safety buffer
    
    return Math.max(totalPageHeight - headerHeight - footerHeight - usedHeight - buffer, 30);
  }
}

export const contentSplitter = new ContentSplitter();